// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// AlertPanel Class

// Construct the alert panel user interface components and provide
// support for any necessary operations.
//
// DESCRIPTION:
//
// @param id a valid CSS selector id for the alert panel and children

define('AlertPanel',['require','ModalWindow','Images','ReturnCodes','EventUtils'],function (require) {
    'use strict';

    var // Require.js module declarations
        ModalWindow = require('ModalWindow'),
        Images = require('Images'),
        ReturnCodes = require('ReturnCodes'),
        EventUtils = require('EventUtils');

    // Constructor
    function AlertPanel(id) {

        var // Dependent resources
            images = new Images(),
            returnCodes = new ReturnCodes(),
            eventUtils = new EventUtils(),

            // Local objects and functions
            title,
            modalWindow,
            defaultTitle = '',
            defaultImageKey = 'warning',
            defaultImage = images.getSrc(defaultImageKey),
            defaultMsg = 'An unknown error occurred!',
            defaultText = 'OK',
            alertMessage = defaultMsg,
            successRC = returnCodes.getCode('SUCCESS_RC'),
            showElementClass = 'show-element',

            // HTML elements
            doc = window.document,
            titleDiv = doc.createElement('div'),
            messageDiv = doc.createElement('div'),
            messageInnerDiv = doc.createElement('div'),
            footerDiv = doc.createElement('div'),
            frameDiv = doc.createElement('div'),
            buttonsDiv = doc.createElement('div'),
            OkButtonDiv = doc.createElement('div'),
            imageDiv = doc.createElement('div'),
            image = doc.createElement('img'),
            div = doc.createElement('div'),

            // Modal Window id
            modalId = 'alert-modal',
            closeDiv;

        // Title
        titleDiv.innerHTML = defaultTitle;

        // Modal Window
        modalWindow = new ModalWindow({
            component: div,
            id: modalId,
            originBounds: {top:'50%', right:'50%', bottom:'50%', left:'50%'},
            useMask: true
        });

        // Add inner message div to parent
        messageDiv.appendChild(messageInnerDiv);

        // Initialize img with warning icon
        image.src = defaultImage;
        imageDiv.classList.add(showElementClass);

        // Initialize OK button with default text
        OkButtonDiv.innerHTML = defaultText;

        // Assemble
        buttonsDiv.appendChild(OkButtonDiv);
        footerDiv.appendChild(buttonsDiv);
        imageDiv.appendChild(image);
        frameDiv.appendChild(imageDiv);
        frameDiv.appendChild(titleDiv);
        frameDiv.appendChild(messageDiv);
        frameDiv.appendChild(footerDiv);
        div.appendChild(frameDiv);
        doc.body.appendChild(modalWindow.getDiv());

        // Assign id for styling
        div.setAttribute('id', id);

        // Close on return or tab
        closeDiv = modalWindow.getCloseDiv();
        closeDiv.tabIndex = 10;

        // Stop external scrolling
        messageDiv.addEventListener('touchmove', function (event) {
            event.stopPropagation();
            if (messageDiv.scrollHeight <= messageDiv.clientHeight) {
                event.preventDefault();
            }
        }.bind(this));

        // Enable the Ok button
        eventUtils.addEventListeners(OkButtonDiv, function (event) {
            modalWindow.close();
            event.stopPropagation();
            // Reset the alert panel
            setTimeout(function () {
                this.reset();
            }.bind(this), 500);
        }.bind(this));

        // Public API

        // Set the title for the message dialog
        this.setTitle = function (newAlertTitle) {
            title = newAlertTitle || defaultTitle;
            titleDiv.innerHTML = title;
        };

        // Set the image for the alert panel
        this.setImage = function (imageURL) {
            if (imageURL == null) {
                image.src = defaultImage;
            // Check if the image is a file in the codebase
            } else if (images.getSrc(imageURL)) {
                image.src = images.getSrc(imageURL);
            // Check if the image src is a valid URL
            } else {
                images.validateSrc(imageURL, function (status) {
                    // If src is valid, set the image src
                    if (status == successRC) {
                        image.src = imageURL;
                    } else {
                        // Hide image altogether
                        imageDiv.classList.remove(showElementClass);
                    }
                });
            }
        };

        // Get the message
        this.getMessage = function () {
            return alertMessage;
        };

        // Set the message
        this.setMessage = function (newAlertMessage) {
            alertMessage = newAlertMessage || defaultMsg;
            messageInnerDiv.innerHTML = alertMessage;
        };

        // Set button text
        this.setOkButtonText = function (okText) {
            OkButtonDiv.innerHTML = okText || defaultText;
        };

        // Reset the alert panel
        this.reset = function() {
            this.setMessage(defaultMsg);
            this.setTitle(defaultTitle);
            this.setImage(defaultImage);
            this.setOkButtonText(defaultText);
        };

        // Show the alert panel
        this.show = function (callback) {
            modalWindow.open(function () {
                closeDiv.focus();
                if (callback) {
                    callback();
                }
            }.bind(this));
        };

        // Hide the alert panel
        this.hide = function (callback) {
            modalWindow.close(function () {
                if (callback) {
                    callback();
                }
            });
        };

        // Get the close button
        this.getCloseDiv = function () {
            return modalWindow.getCloseDiv();
        };

        // Get the containing div for the panel
        this.getDiv = function () {
            return modalWindow.getDiv();
        };
    };

    return AlertPanel.prototype.constructor;
});

